import { gql } from "lib/apolloClient";
import {
  globalCategoryFragment,
  gradingConfigurationFragment,
  gradingCategorySetsFragment,
  cumulativeTermWeightsFragment,
  gradeScaleDetailFragment,
  compactGradeScaleDetailFragment,
  plannerElementNodeSetFragment,
  levelsDetailFragment,
  basicUserDetailsFragment,
  gradeScaleDetailFragmentWithPresetScoreValues,
} from "./GradingSetupV2Fragments";

export const getAcademicYearDetailsQuery = gql`
  query getAcademicYearDetails($id: ID!, $filters: TermFilters) {
    node(id: $id, type: ACADEMIC_YEAR) {
      id
      ... on AcademicYear {
        id
        terms(filters: $filters) {
          id
          label
          startDate
          endDate
          type
          lockingStatus
          associatedAcademicTermEntities {
            id
            academicTerm {
              id
              label
            }
          }
        }
      }
    }
  }
`;

export const getGradingConfigQuery = gql`
  query getGradingConfig(
    $id: ID!
    $configFilters: GradingConfigurationFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gradingConfiguration(filters: $configFilters) {
          edges {
            node {
              id
              ...GradingConfigurationFragment
              ...GradingCategorySetsFragment
              ...CumulativeTermWeightsFragment
            }
          }
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
  ${cumulativeTermWeightsFragment}
`;

export const getCreditsV2Query = gql`
  query getCreditsV2($id: ID!, $filters: GradeSubjectTermCreditMapFilters) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        creditsV2(filters: $filters) {
          id
          credit
          subject {
            id
            name
          }
          grade {
            id
            name
          }
          learningCourse {
            id
            title
          }
          academicTerm {
            id
            label
          }
          genericTag {
            id
            label
            abbreviation
          }
        }
      }
    }
  }
`;

export const getGpaInclusionMapQuery = gql`
  query getGpaInclusionMap($id: ID!, $filters: GPAInclusionMapFilters) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gpaInclusionMap(filters: $filters) {
          id
          type
          grade {
            id
            name
          }
          subject {
            id
            name
          }
          learningCourse {
            id
            title
          }
        }
      }
    }
  }
`;

export const getGpaAddedValuesV2Query = gql`
  query getGpaAddedValuesV2($id: ID!, $filters: GPAddedValueFilters) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gpaAddedValuesV2(filters: $filters) {
          id
          value
          grade {
            id
            name
          }
          subject {
            id
            name
          }
          learningCourse {
            id
            title
          }
          genericTag {
            id
            label
            abbreviation
          }
        }
      }
    }
  }
`;

export const getGlobalCategoriesQuery = gql`
  query getGradingCategories($id: ID!, $filters: GlobalCategoryFilters) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        globalCategory(filters: $filters) {
          edges {
            node {
              ...GlobalCategoryFragment
            }
          }
        }
      }
    }
  }
  ${globalCategoryFragment}
`;

export const getGradeScaleDetailsQuery = gql`
  query getGradeScales(
    $id: ID!
    $filters: GradeScaleFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      ... on CurriculumProgram {
        id
        gradeScale(filters: $filters) {
          edges {
            node {
              ...GradeScaleFragment
            }
          }
        }
      }
    }
  }
  ${gradeScaleDetailFragment}
`;

export const getGradeScaleForFinalGradeDetailsQuery = gql`
  query getGradeScales(
    $id: ID!
    $filters: GradeScaleFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      ... on CurriculumProgram {
        id
        gradeScale(filters: $filters) {
          edges {
            node {
              ...GradeScaleFragment
            }
          }
        }
      }
    }
  }
  ${compactGradeScaleDetailFragment}
`;

export const getOrganizationLearningCoursesQuery = gql`
  query getOrganizationLearningCourses(
    $organizationId: ID!
    $learningCourseFilters: LearningCourseFilters
    $first: Int
    $after: String
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        learningCourses(
          filters: $learningCourseFilters
          first: $first
          after: $after
        ) {
          count {
            totalCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              academicCourse {
                label
              }
              code
              type
              grades {
                id
                name
              }
              gradingPeriods {
                edges {
                  node {
                    id
                  }
                }
              }
              subjects {
                id
                name
              }
              lockingInfo {
                pageTypes
              }
              displayMetadata
              associatedLevels {
                id
              }
              gradingMethodology {
                gradingMethodologyTypes
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationLearningCoursesForFinalGradeQuery = gql`
  query getOrganizationLearningCoursesForFinalGrade(
    $organizationId: ID!
    $learningCourseFilters: LearningCourseFilters
    $first: Int
    $after: String
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        learningCourses(
          filters: $learningCourseFilters
          first: $first
          after: $after
        ) {
          count {
            totalCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              academicCourse {
                label
              }
              code
              type
              grades {
                id
                name
              }
              gradingPeriods {
                edges {
                  node {
                    id
                  }
                }
              }
              subjects {
                id
                name
              }
              lockingInfo {
                pageTypes
              }
              displayMetadata
              gradingMethodology {
                gradingMethodologyTypes
                id
              }
              gradeScales {
                ...GradeScaleFragment
              }
            }
          }
        }
      }
    }
  }
  ${compactGradeScaleDetailFragment}
`;

export const getSubjectGradingMethodologyQuery = gql`
  query getSubjectGradingMethodology(
    $id: ID!
    $academicYearId: ID!
    $subjectFilters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $subjectFilters) {
          id
          name
          grades {
            id
            name
          }
          gradingMethodologies(filters: { academicYearId: $academicYearId }) {
            id
            entityDetail {
              gradeId
            }
            gradingMethodologyTypes
          }
          associatedLevels {
            id
          }
          flatVariants(filters: { archivalState: ACTIVE }) {
            level {
              id
            }
          }
        }
      }
    }
  }
`;

export const getSubjectIdsForFinalGradeQuery = gql`
  query getSubjectIdsForFinalGrade(
    $id: ID!
    $subjectFilters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $subjectFilters) {
          id
          grades {
            id
          }
        }
      }
    }
  }
`;

export const getSubjectsByIdsForFinalGradeQuery = gql`
  query getSubjectsByIdsForFinalGrade(
    $ids: [ID!]
    $academicYearId: ID!
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    multiNode(ids: $ids, type: SUBJECT) {
      id
      ... on Subject {
        id
        name
        gradeScales {
          ...GradeScaleFragment
        }
        grades {
          id
          name
        }
        gradingMethodologies(filters: { academicYearId: $academicYearId }) {
          id
          entityDetail {
            gradeId
          }
          gradingMethodologyTypes
        }
      }
    }
  }
  ${compactGradeScaleDetailFragment}
`;

export const getOrganizationSubjectsQuery = gql`
  query getOrganizationSubjects($id: ID!, $filters: SubjectFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        subjects(filters: $filters) {
          id
          name
          grades {
            id
            name
          }
        }
      }
    }
  }
`;

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades($id: ID!, $gradeFilters: GradeFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        grades(filters: $gradeFilters) {
          id
          name
        }
      }
    }
  }
`;

export const getGradingPeriodsQuery = gql`
  query getGradingPeriods($id: ID!, $filters: TermFilters!) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      ... on CurriculumProgram {
        id
        gradingPeriods(filters: $filters) {
          edges {
            node {
              id
              label
              startDate
              endDate
              lockingStatus
            }
          }
        }
      }
    }
  }
`;

export const getCurriculumGradingMethodologyTypes = gql`
  query getCurriculumGradingMethodologyType(
    $id: ID!
    $gradingMethodologyFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gradingMethodologies(filters: $gradingMethodologyFilters) {
          id
          gradingMethodologyTypes
        }
      }
    }
  }
`;

export const getStandardSetsQuery = gql`
  query getStandardSets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: $type) {
      ... on CurriculumProgram {
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          totalCount
          edges {
            node {
              ...PlannerElementNodeSetFragment
            }
          }
        }
      }
    }
  }
  ${plannerElementNodeSetFragment}
`;

export const getGradesScaleListQuery = gql`
  query getGradesScaleList($id: ID!, $filters: GradeScaleFilters) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gradeScale(filters: $filters) {
          edges {
            node {
              id
              templateName
            }
          }
        }
      }
    }
  }
`;

export const getGradingConfigSettingsQuery = gql`
  query getGradingConfigSettings(
    $id: ID!
    $configFilters: GradingConfigurationFilters
    $gradingMethodologyFilters: GradingSetupEntityFilters
    $skipGradingMethodologies: Boolean = false
    $includeCumulativeTermWeights: Boolean! = false
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gradingMethodologies(filters: $gradingMethodologyFilters)
          @skip(if: $skipGradingMethodologies) {
          id
          gradingMethodologyTypes
        }
        gradingConfiguration(filters: $configFilters) {
          edges {
            node {
              id
              ...GradingConfigurationFragment
              ...CumulativeTermWeightsFragment
                @include(if: $includeCumulativeTermWeights)
            }
          }
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${cumulativeTermWeightsFragment}
`;

export const getLearningCourseGradingConfigsQuery = gql`
  query getLearningCourseGradingConfigs(
    $organizationId: ID!
    $learningCourseFilters: LearningCourseFilters
    $gradingConfigFilters: GradingConfigurationFilters
    $first: Int
    $after: String
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        learningCourses(
          filters: $learningCourseFilters
          first: $first
          after: $after
        ) {
          count {
            totalCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              academicCourse {
                label
              }
              displayMetadata
              code
              type
              lockingInfo {
                pageTypes
              }
              gradingPeriods {
                edges {
                  node {
                    id
                  }
                }
              }
              gradingConfigurations(filters: $gradingConfigFilters) {
                id
                isDefault
                updatedAt
                createdAt
                updatedBy {
                  ...BasicUserDetailsFragment
                }
                createdBy {
                  ...BasicUserDetailsFragment
                }
                ...GradingConfigurationFragment
                ...GradingCategorySetsFragment
                ...CumulativeTermWeightsFragment
              }
            }
          }
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
  ${cumulativeTermWeightsFragment}
  ${basicUserDetailsFragment}
`;

export const getSubjectGradingConfigsQuery = gql`
  query getSubjectsGradingConfig(
    $ids: [ID!]
    $gradingConfigFilters: SubjectGradingConfigurationFilters
  ) {
    multiNode(ids: $ids, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingConfigurations(filters: $gradingConfigFilters) {
          id
          isDefault
          updatedAt
          createdAt
          updatedBy {
            ...BasicUserDetailsFragment
          }
          createdBy {
            ...BasicUserDetailsFragment
          }
          entityDetail {
            subjectId
            gradeId
          }
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
          ...CumulativeTermWeightsFragment
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
  ${cumulativeTermWeightsFragment}
  ${basicUserDetailsFragment}
`;

export const getLearningCourseIdsQuery = gql`
  query getLearningCourseGradingConfigs(
    $organizationId: ID!
    $learningCourseFilters: LearningCourseFilters
    $first: Int
    $after: String
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        learningCourses(
          filters: $learningCourseFilters
          first: $first
          after: $after
        ) {
          count {
            totalCount
          }
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getSubjectsListingQuery = gql`
  query getSubjectsListing(
    $id: ID!
    $subjectFilters: SubjectFilterInput
    $subjectGradingMethodologyFilters: SubjectGradingMethodologyFilters
    $isGradingMethodology: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $subjectFilters) {
          id
          name
          grades {
            id
            name
          }
          gradingMethodologies(filters: $subjectGradingMethodologyFilters)
            @include(if: $isGradingMethodology) {
            id
            entityDetail {
              gradeId
            }
            gradingMethodologyTypes
          }
        }
      }
    }
  }
`;

export const getCurriculumLevelsQuery = gql`
  query getCurriculumLevels(
    $curriculumProgramId: ID!
    $curriculumType: ENTITY_TYPE_ENUM!
    $levelsFilters: GenericTagFilters
  ) {
    node(id: $curriculumProgramId, type: $curriculumType) {
      id
      ... on IbPypCurriculumProgram {
        id
        levels(filters: $levelsFilters) {
          ...LevelsDetailFragment
        }
      }
      ... on IbMypCurriculumProgram {
        id
        levels(filters: $levelsFilters) {
          ...LevelsDetailFragment
        }
      }
      ... on UBDCurriculumProgram {
        id
        levels(filters: $levelsFilters) {
          ...LevelsDetailFragment
        }
      }
      ... on IbDpCurriculumProgram {
        id
        levels(filters: $levelsFilters) {
          ...LevelsDetailFragment
        }
      }
      ... on GenericCurriculumProgram {
        id
        levels(filters: $levelsFilters) {
          ...LevelsDetailFragment
        }
      }
    }
  }
  ${levelsDetailFragment}
`;

export const getOrganizationGenericTagsQuery = gql`
  query getOrganizationGenericTags(
    $organizationId: ID!
    $filters: GenericTagsFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        genericTagsV2(filters: $filters) {
          ...LevelsDetailFragment
        }
      }
    }
  }
  ${levelsDetailFragment}
`;

export const getLearningCoursesByIdsQuery = gql`
  query getLearningCoursesByIds($ids: [ID!]) {
    multiNode(ids: $ids, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        title
        academicCourse {
          label
        }
        code
        type
        grades {
          id
          name
        }
        gradingPeriods {
          edges {
            node {
              id
            }
          }
        }
        subjects {
          id
          name
        }
        gradingMethodology {
          gradingMethodologyTypes
          id
        }
        displayMetadata
        lockingInfo {
          pageTypes
        }
      }
    }
  }
`;

export const getMultiCourseGradingConfigAndCategorySetsQuery = gql`
  query getMultiCourseGradingConfigAndCategorySets(
    $ids: [ID!]
    $gradingConfigFilters: GradingConfigurationFilters
  ) {
    multiNode(ids: $ids, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradingMethodology {
          id
          gradingMethodologyTypes
        }
        gradingConfigurations(filters: $gradingConfigFilters) {
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
`;

export const getMultiSubjectsGradingConfigAndCategorySetsQuery = gql`
  query getMultiSubjectsGradingConfigAndCategorySets(
    $ids: [ID!]
    $gradingConfigFilters: SubjectGradingConfigurationFilters
    $gradingMethodologyFilters: SubjectGradingMethodologyFilters
  ) {
    multiNode(ids: $ids, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingMethodologies(filters: $gradingMethodologyFilters) {
          id
          gradingMethodologyTypes
          entityDetail {
            gradeId
            subjectId
          }
        }
        gradingConfigurations(filters: $gradingConfigFilters) {
          id
          type
          entityDetail {
            subjectId
            gradeId
          }
          settings {
            id
            uid
            groupType
            value
          }
          ...GradingCategorySetsFragment
        }
      }
    }
  }
  ${gradingCategorySetsFragment}
`;

export const getGradeScaleTemplateNamesQuery = gql`
  query getGradeScaleTemplateNames(
    $id: ID!
    $gradeScaleFilters: GradeScaleFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gradeScale(filters: $gradeScaleFilters) {
          edges {
            node {
              id
              type
              templateName
            }
          }
        }
      }
    }
  }
`;

// * for getting learningCourse or Subject grading configurations from the assessment by id
export const getAssessmentGradingConfigurationsQuery = gql`
  query getAssessmentGradingConfigurations($id: ID!) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        gradingConfigurations {
          id
          settings {
            id
            uid
            value
          }
        }
      }
    }
  }
`;

export const getMultiSubjectsGradingConfigQuery = gql`
  query getMultiSubjectsGradingConfig(
    $ids: [ID!]
    $filters: SubjectGradingConfigurationFilters
    $gradingMethodologyFilters: SubjectGradingMethodologyFilters
  ) {
    multiNode(ids: $ids, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingMethodologies(filters: $gradingMethodologyFilters) {
          id
          gradingMethodologyTypes
          entityDetail {
            gradeId
            subjectId
          }
        }
        gradingConfigurations(filters: $filters) {
          ...GradingConfigurationFragment
          entityDetail {
            gradeId
            subjectId
          }
        }
      }
    }
  }
  ${gradingConfigurationFragment}
`;

export const getCourseGradingConfigQuery = gql`
  query getCourseGradingConfig(
    $id: ID!
    $filters: GradingConfigurationFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradingMethodology {
          id
          gradingMethodologyTypes
        }
        gradingConfigurations(filters: $filters) {
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
        }
      }
    }
  }

  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
`;

export const getSubjectGradingConfigQuery = gql`
  query getSubjectGradingConfig(
    $id: ID!
    $filters: SubjectGradingConfigurationFilters
    $gradingMethodologyFilters: SubjectGradingMethodologyFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingMethodologies(filters: $gradingMethodologyFilters) {
          id
          gradingMethodologyTypes
          entityDetail {
            subjectId
            gradeId
          }
        }
        gradingConfigurations(filters: $filters) {
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
`;

export const getMYPIBDefinedGradeScalesQuery = gql`
  query getGradeScales(
    $id: ID!
    $filters: GradeScaleFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: IB_MYP_CURRICULUM_PROGRAM) {
      ... on IbMypCurriculumProgram {
        id
        gradeScale(filters: $filters) {
          edges {
            node {
              ...GradeScaleFragment
            }
          }
        }
      }
    }
  }
  ${gradeScaleDetailFragment}
`;

export const getPlannerElementSetsForCurriculumQuery = gql`
  query getPlannerElementSetsForCurriculum(
    $id: ID!
    $plannerElementFilters: PlannerElementNodeSetFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementFilters) {
          edges {
            node {
              id
              type
              title
              gradeScales {
                id
                displayName
                templateName
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationGlobalGenericTagsQuery = gql`
  query getOrganizationGlobalGenericTags(
    $globalTagFilters: GlobalGenericTagFilters
  ) {
    globalConstants {
      globalGenericTags(filters: $globalTagFilters) {
        id
        label
        type
      }
    }
  }
`;

export const getGradeScaleDetailsByIdQuery = gql`
  query getGradeScaleDetailsById(
    $id: ID!
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: GRADE_SCALE) {
      id
      ... on GradeScale {
        id
        ...GradeScaleFragment
      }
    }
  }
  ${gradeScaleDetailFragmentWithPresetScoreValues}
`;

export const getGradeScaleEntityDetailsByIdQuery = gql`
  query getGradeScaleEntityDetailsById(
    $id: ID!
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: GRADE_SCALE) {
      id
      ... on GradeScale {
        id
        templateName
        gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
          entityDetail {
            learningCourse {
              id
              title
            }
            subject {
              id
              name
            }
            grade {
              id
              name
            }
          }
        }
      }
    }
  }
`;
